import { QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell.js'
import { ReleaseProvider } from './components/UpdateNotice.js'
import { OpenJobFromPath } from './components/JobOverlay.js'
import { FieldInbox } from './components/field/FieldInbox.js'
import { FieldJobs } from './components/field/FieldJobs.js'
import { FieldMe } from './components/field/FieldMe.js'
import { FieldTeam } from './components/field/FieldTeam.js'
import { BootHold, LoadingPanel } from './components/Loader.js'
import { useIsField } from './lib/media.js'
import { RealtimeProvider } from './lib/realtime.js'
import { SessionProvider, useSession } from './lib/session.js'
import { TRPCProvider, makeQueryClient, trpcClient } from './lib/trpc.js'
import { Login } from './routes/Login.js'
import { Today } from './routes/Today.js'

/**
 * A screen in a chunk of its own, loaded the first time it is opened.
 *
 * The first load is Today, the field app and the job card — what a
 * technician's phone needs at 7 am on a mobile connection. The office's
 * screens, the customer's pages and the board each come when asked for, and
 * a desk warms the office's in the background once it is idle
 * (`warmOfficeScreens`), so moving around there never waits on the network.
 */
function screen<Module, Key extends keyof Module>(load: () => Promise<Module>, key: Key) {
  return {
    load,
    Page: lazy(() => load().then((module) => ({ default: module[key] as ComponentType }))),
  }
}

// The scheduling module pulls in dnd-kit and MapLibre — the map renderer alone
// is larger than the rest of the app — and it is a desktop screen most
// technicians never open.
const Schedule = screen(() => import('./routes/schedule/Schedule.js'), 'Schedule')
const RoutePlanner = screen(() => import('./routes/schedule/planner/RoutePlanner.js'), 'RoutePlanner')
// The help centre (decision 115) is read now and then.
const HelpCentre = screen(() => import('./guide/HelpCentre.js'), 'HelpCentre')

// The office.
const Jobs = screen(() => import('./routes/Jobs.js'), 'Jobs')
const Inbox = screen(() => import('./routes/Inbox.js'), 'Inbox')
const Tasks = screen(() => import('./routes/Tasks.js'), 'Tasks')
const Clients = screen(() => import('./routes/Clients.js'), 'Clients')
const ClientDetail = screen(() => import('./routes/ClientDetail.js'), 'ClientDetail')
const ClientDuplicates = screen(() => import('./routes/ClientDuplicates.js'), 'ClientDuplicates')
const ClientSortOut = screen(() => import('./routes/ClientSortOut.js'), 'ClientSortOut')
const Agreements = screen(() => import('./routes/Agreements.js'), 'Agreements')
const AgreementDetail = screen(() => import('./routes/AgreementDetail.js'), 'AgreementDetail')
const Billing = screen(() => import('./routes/billing/Billing.js'), 'Billing')
const Quotes = screen(() => import('./routes/Quotes.js'), 'Quotes')
const Invoices = screen(() => import('./routes/Invoices.js'), 'Invoices')
const Materials = screen(() => import('./routes/Materials.js'), 'Materials')
const PersonDetail = screen(() => import('./routes/PersonDetail.js'), 'PersonDetail')
const Settings = screen(() => import('./routes/Settings.js'), 'Settings')
const EcoPage = screen(() => import('./routes/EcoPage.js'), 'EcoPage')

// The customer's pages and the account pages nobody signed in needs.
const ProposalView = screen(() => import('./routes/public/ProposalView.js'), 'ProposalView')
const InvoiceView = screen(() => import('./routes/public/InvoiceView.js'), 'InvoiceView')
const BookingView = screen(() => import('./routes/public/BookingView.js'), 'BookingView')
const PickTimeView = screen(() => import('./routes/public/PickTimeView.js'), 'PickTimeView')
const Connect = screen(() => import('./routes/Connect.js'), 'Connect')
const AcceptInvite = screen(() => import('./routes/AcceptInvite.js'), 'AcceptInvite')
const ForgotPassword = screen(() => import('./routes/ForgotPassword.js'), 'ForgotPassword')
const ResetPassword = screen(() => import('./routes/ResetPassword.js'), 'ResetPassword')

const OFFICE = [
  Jobs, Inbox, Tasks, Clients, ClientDetail, Agreements, AgreementDetail,
  Billing, Quotes, Invoices, Settings, PersonDetail, Schedule,
]

/**
 * Fetches the office's screens once a desk has drawn its first one and gone
 * quiet. Not on the field app: a phone fetches only what it opens.
 */
function warmOfficeScreens(): () => void {
  const idle = window.requestIdleCallback ?? ((run: () => void) => window.setTimeout(run, 1500))
  const cancel = window.cancelIdleCallback ?? window.clearTimeout
  const handle = idle(() => {
    for (const { load } of OFFICE) void load().catch(() => undefined)
  })
  return () => cancel(handle)
}

/** A screen from its own chunk, with the loader while it arrives. */
function Page({ of, fallback = <LoadingPanel className="h-full" /> }: { of: { Page: ComponentType }; fallback?: ReactNode }) {
  return (
    <Suspense fallback={fallback}>
      <of.Page />
    </Suspense>
  )
}

export function App() {
  const [queryClient] = useState(makeQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <ReleaseProvider>
              <Shell />
            </ReleaseProvider>
          </SessionProvider>
        </BrowserRouter>
      </TRPCProvider>
    </QueryClientProvider>
  )
}

function Shell() {
  const { user, isLoading } = useSession()
  const field = useIsField()

  useEffect(() => (user && !field ? warmOfficeScreens() : undefined), [user, field])

  return (
    <RealtimeProvider enabled={Boolean(user)}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<Page of={ForgotPassword} />} />
        <Route path="/reset-password" element={<Page of={ResetPassword} />} />
        <Route path="/accept-invite" element={<Page of={AcceptInvite} />} />
        {/* A customer's proposal link. Outside the signed-in shell on
            purpose: the token is the credential. Decision 80. */}
        <Route path="/p/:token" element={<Page of={ProposalView} />} />
        {/* The same for an invoice's link. Decision 88. */}
        <Route path="/i/:token" element={<Page of={InvoiceView} />} />
        {/* A customer's booking link: confirm, or ask for another time. Decision 104. */}
        <Route path="/b/:token" element={<Page of={BookingView} />} />
        {/* A pick-a-time link from the Booking desk. Decision 106. */}
        <Route path="/pick/:token" element={<Page of={PickTimeView} />} />
        {/* An outside AI app asking to act as the person. Decision 116. */}
        <Route path="/connect" element={<Page of={Connect} />} />

        <Route
          element={
            isLoading ? <BootHold /> : user ? <AppShell /> : <RedirectToLogin />
          }
        >
          <Route index element={<Today />} />
          <Route path="jobs" element={<ByDevice field={<FieldJobs />} desk={<Page of={Jobs} />} />} />
          <Route path="worklist" element={<Page of={Jobs} />} />
          <Route path="team" element={<ByDevice field={<FieldTeam />} desk={<Navigate to="/board" replace />} />} />
          <Route
            path="me"
            element={<ByDevice field={<FieldMe />} desk={<Navigate to="/settings?tab=account" replace />} />}
          />

          {/* A job has no page. It opens as a popup over whatever screen you
              are on, from `?jobId=`, drawn by `JobOverlay` in the shell. These
              two are the addresses that used to be pages, kept working for
              links already sent. Decision 61. */}
          <Route path="jobs/:jobId" element={<OpenJobFromPath />} />
          <Route path="jobs/:jobId/report/:jobServiceId" element={<OpenJobFromPath />} />

          <Route path="board" element={<Page of={Schedule} />} />
          <Route path="board/plan" element={<Page of={RoutePlanner} />} />
          <Route path="inbox" element={<ByDevice field={<FieldInbox />} desk={<Page of={Inbox} />} />} />
          <Route path="tasks" element={<Page of={Tasks} />} />
          <Route path="clients" element={<Page of={Clients} />} />
          <Route path="clients/duplicates" element={<Page of={ClientDuplicates} />} />
          <Route path="clients/sort-out" element={<Page of={ClientSortOut} />} />
          <Route path="clients/:id" element={<Page of={ClientDetail} />} />
          <Route path="agreements" element={<Page of={Agreements} />} />
          <Route path="agreements/:id" element={<Page of={AgreementDetail} />} />
          {/* Billing (decision 111). The two registers live under it, and their
              old addresses still land there with their filters. */}
          <Route path="billing" element={<Page of={Billing} />} />
          <Route path="billing/quotes" element={<Page of={Quotes} />} />
          <Route path="billing/invoices" element={<Page of={Invoices} />} />
          <Route path="quotes" element={<MovedToBilling to="/billing/quotes" />} />
          <Route path="invoices" element={<MovedToBilling to="/billing/invoices" />} />
          <Route path="materials" element={<Page of={Materials} />} />
          <Route path="users" element={<Navigate to="/settings?tab=people" replace />} />

          {/* A person, unlike a job, does have a page. Rule 13 is about jobs:
              a job is glanced at over the board, and a person is a record you
              sit with — long enough to photograph a licence card into it. */}
          <Route path="people/:userId" element={<Page of={PersonDetail} />} />

          <Route path="settings" element={<Page of={Settings} />} />
          {/* Eco as a page (decision 116). */}
          <Route path="eco" element={<Page of={EcoPage} />} />
          <Route path="help/*" element={<Page of={HelpCentre} fallback={null} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  )
}

/**
 * `/quotes` and `/invoices`, kept working: the register, with whatever filter
 * the link carried. "Chase overdue invoices" lands on Billing's Overdue step,
 * which is where chasing is done now.
 */
function MovedToBilling({ to }: { to: string }) {
  const location = useLocation()
  const search = new URLSearchParams(location.search)
  if (search.get('overdueOnly') === 'true') {
    search.delete('overdueOnly')
    search.set('stage', 'OVERDUE')
    return <Navigate to={{ pathname: '/billing', search: `?${search}` }} replace />
  }
  return <Navigate to={{ pathname: to, search: location.search }} replace />
}

/**
 * A route the field app draws differently. The same test as the shell's, so a
 * screen and its frame always agree. Decision 93.
 */
function ByDevice({ field, desk }: { field: ReactNode; desk: ReactNode }) {
  return <>{useIsField() ? field : desk}</>
}

/**
 * Keeps the destination so signing in lands where the person was headed.
 *
 * This is what makes a shared link work for someone who is not signed in:
 * they get the login page, then the page they were sent, rather than the
 * dashboard and a second trip through their messages to find the link again.
 */
function RedirectToLogin() {
  const location = useLocation()
  const next = `${location.pathname}${location.search}`

  return (
    <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  )
}
